import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalizedNav } from '../i18n/useLocalizedNav';
import { serifFont } from '../i18n/fonts';
import ShippouEffect from './visualEffects/ShippouEffect';
import ShippouGoldEffect from './visualEffects/ShippouGoldEffect';
import TechMeshEffect from './visualEffects/TechMeshEffect';
import WaveEffect from './visualEffects/WaveEffect';
import FujiEffect from './visualEffects/FujiEffect';
import AyubitoHeroSlide from './AyubitoHeroSlide';

const HeroSlide = ({ slideData, isActive }) => {
  const [showKanji, setShowKanji] = useState(false);
  const [showTitle, setShowTitle] = useState(false);
  const { t } = useTranslation();
  const { lang, navigate } = useLocalizedNav();

  useEffect(() => {
    if (isActive) {
      // アクティブになったらテキストアニメーション開始
      const kanjiTimer = setTimeout(() => setShowKanji(true), 500);
      const titleTimer = setTimeout(() => setShowTitle(true), 1000);

      return () => {
        clearTimeout(kanjiTimer);
        clearTimeout(titleTimer);
      };
    } else {
      // 非アクティブになったらリセット
      setShowKanji(false);
      setShowTitle(false);
    }
  }, [isActive]);

  const renderBackground = () => {
    switch (slideData.backgroundType) {
      case 'animation':
        switch (slideData.visualType) {
          case 'shippou':
            return <ShippouEffect />;
          case 'shippouGold':
            return <ShippouGoldEffect />;
          case 'techMesh':
            return <TechMeshEffect />;
          case 'wave':
            return <WaveEffect />;
          case 'fuji':
            return <FujiEffect />;
          default:
            return null;
        }
      case 'image':
        return (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${slideData.backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }} />
        );
      case 'video':
        return (
          <video
            autoPlay
            muted
            loop
            playsInline
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          >
            <source src={slideData.backgroundVideo} type="video/mp4" />
          </video>
        );
      case 'gradient':
        return (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: slideData.backgroundGradient,
          }} />
        );
      default:
        return null;
    }
  };

  const styles = {
    slideContainer: {
      position: 'relative',
      flex: '0 0 100%',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
    },
    contentOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 30,
    },
    heroContent: {
      textAlign: slideData.textPosition || 'center',
      padding: '0 20px',
      position: 'relative',
    },
    textBlock: {
      background: slideData.textBlockBackground 
        ? (slideData.textBlockBackgroundColor || 'rgba(0, 0, 0, 0.5)')
        : 'transparent',
      padding: slideData.textBlockBackground 
        ? (slideData.textBlockPadding || '30px 50px')
        : '0',
      borderRadius: slideData.textBlockBackground 
        ? (slideData.textBlockBorderRadius || '8px')
        : '0',
      backdropFilter: slideData.textBlockBackground ? 'blur(5px)' : 'none',
      transition: 'all 0.3s',
    },
    heroTitleContainer: {
      position: 'relative',
      minHeight: '70px',
      marginBottom: '70px',
    },
    kanjiLeft: {
      position: 'absolute',
      left: '-200px',
      top: '50%',
      transform: 'translateY(-50%)',
      fontSize: '300px',
      fontWeight: 'bold',
      color: 'rgba(255, 255, 255, 0.15)',
      fontFamily: "'Noto Serif JP', serif",
      opacity: showKanji ? 1 : 0,
      filter: showKanji ? 'blur(0px)' : 'blur(20px)',
      transition: 'all 2s ease-out',
      zIndex: 1,
      userSelect: 'none',
    },
    kanjiRight: {
      position: 'absolute',
      right: '-200px',
      top: '50%',
      transform: 'translateY(-50%)',
      fontSize: '300px',
      fontWeight: 'bold',
      color: 'rgba(255, 255, 255, 0.15)',
      fontFamily: "'Noto Serif JP', serif",
      opacity: showKanji ? 1 : 0,
      filter: showKanji ? 'blur(0px)' : 'blur(20px)',
      transition: 'all 2s ease-out',
      zIndex: 1,
      userSelect: 'none',
    },
    heroTitle: {
      fontSize: '56px',
      fontWeight: 'bold',
      marginBottom: '20px',
      fontFamily: slideData.titleFont === 'serif' ? serifFont(lang) : undefined,
      color: slideData.textColor || '#ffffff',
      opacity: showTitle ? 1 : 0,
      transform: showTitle ? 'translateY(0)' : 'translateY(20px)',
      transition: 'all 1s ease-out',
      position: 'relative',
      zIndex: 2,
    },
    heroSubtitle: {
      fontSize: '24px',
      marginBottom: '40px',
      color: slideData.textColor || '#ffffff',
      opacity: showTitle ? 1 : 0,
      transform: showTitle ? 'translateY(0)' : 'translateY(20px)',
      transition: 'all 1s ease-out 0.2s',
    },
    heroButton: {
      backgroundColor: 'transparent',
      color: slideData.textColor || '#ffffff',
      border: `2px solid ${slideData.textColor || '#ffffff'}`,
      padding: '15px 40px',
      fontSize: '16px',
      borderRadius: '50px',
      cursor: 'pointer',
      fontWeight: 'bold',
      transition: 'all 0.3s',
      opacity: showTitle ? 1 : 0,
      transform: showTitle ? 'translateY(0)' : 'translateY(20px)',
      transitionDelay: '0.4s',
    },
  };

  const handleButtonClick = () => {
    if (!slideData.buttonAction) return;

    const action = slideData.buttonAction;

    // ページ遷移の場合 (例: 'news', 'about', 'news/3')
    if (action.includes('/')) {
      // news/3 のような形式の場合
      const parts = action.split('/');
      const page = parts[0]; // 'news'
      const id = parts[1] ? parseInt(parts[1]) : null; // 3

      if (page === 'news' && id) {
        // ニュース詳細ページへ
        navigate(`/news/${id}`);
      } else {
        // その他のページへ
        navigate(`/${page}`);
      }
    } else if (action === 'news' || action === 'about') {
      // 単純なページ遷移
      navigate(`/${action}`);
    } else if (action === 'home') {
      // ホームページへ
      navigate('/');
    } else {
      // スクロール先のIDとして扱う (work, contact など)
      // まずホームページに移動してからスクロール
      navigate('/');
      setTimeout(() => {
        const section = document.getElementById(action);
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  // AYUBITO 専用レイアウトのスライド
  if (slideData.layout === 'ayubito') {
    return (
      <div style={styles.slideContainer}>
        <AyubitoHeroSlide slideData={slideData} isActive={isActive} />
      </div>
    );
  }

  return (

    <div style={styles.slideContainer}>
      {/* 背景 */}
      {renderBackground()}

      {/* コンテンツオーバーレイ */}
      <div style={styles.contentOverlay}>
        <div style={styles.heroContent}>
          <div style={styles.textBlock}>
            <div style={styles.heroTitleContainer}>
              <h1 style={styles.heroTitle} className="hero-title-main">
                {t(slideData.titleKey)}
              </h1>
            </div>
            <p style={styles.heroSubtitle} className="hero-subtitle-main">
              {t(slideData.subtitleKey)}
            </p>
            {slideData.buttonTextKey && (
              <button
                onClick={handleButtonClick}
                style={styles.heroButton}
                className="hero-button-main"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = slideData.textColor || '#ffffff';
                  e.currentTarget.style.color = slideData.backgroundType === 'animation' ? '#1e3a8a' : '#000000';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = slideData.textColor || '#ffffff';
                }}
              >
                {t(slideData.buttonTextKey)}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSlide;